import * as React from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { RolesContext } from 'src/Roles/RolesContext';

import { RAM_COMPONENT_NAME } from 'src/ServerEntities/IConfiguration';
import MenuLink from '../Components/MenuLink';

const TopBar = styled.header`
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    padding: 10px 16px;
    @media (max-width: 1279px) {
        flex-direction: column;
        align-items: stretch;
        padding: 0;
    }
`;

const ActionContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
    @media (max-width: 1279px) {
        display: none;
    }
`;

const MobileNavContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    background: #396282;
    width: 100%;
    position: absolute;
    top: 64px;
    z-index: 2;
    & a {
        font-size: 1.4rem;
        text-align: center;
        padding: 8px 4px;
        flex: 1 1 auto;
        border: 0 none;
    }
`;

const MobileLink = styled.a`
    cursor: pointer;
`;

interface IProps {
    pageName: string,
};

const RhapsodyMenus = (props: IProps) => {
    const { pageName } = props;
    const [displayMobileNav, setDisplayMobileNav] = React.useState(false);
    const roleContext = React.useContext(RolesContext);
    const configuration = roleContext.state.ramConfiguration;
    const components = configuration?.components || [];
    const navigate = useNavigate();

    React.useEffect(() => {
        if (configuration && components.length > 0) {
            const isDashboardPage = pageName.includes('Dashboard');
            
            if (isDashboardPage) {
                if (components.map(c=>c.name).includes('RhapsodyDashboard')) {
                    navigate('/Ram/Rhapsody Dashboard');
                } else if (components.map(c=>c.name).includes('IntersystemsDashboard')) {
                    navigate('/Ram/Intersystems Dashboard');
                } else if (components.map(c=>c.name).includes('QVeraDashboard')) {
                    navigate('/Ram/QVera Dashboard');
                }
            }
        }
    }, [configuration, components, navigate]);


    const onLinkClick = (tabName: string) => () => {
        setDisplayMobileNav(false);
        navigate(`/Ram/${tabName}`);
    };


    const getComponentForMobile = (componentName: RAM_COMPONENT_NAME, index: number) => {
        switch (componentName) {
            case "RhapsodyDashboard":
                return <MobileLink onClick={onLinkClick("RhapsodyDashboard")} className={pageName === "Rhapsody Dashboard" ? "selected header-link" : "header-link"}>Dashboard</MobileLink>
            case "MonitorServers":
                return <MobileLink onClick={onLinkClick("MonitorServers")} className={pageName === "Servers" ? "selected header-link" : "header-link"}>Servers</MobileLink>
            default:
                return null;
        }
    };

    const getComponent = (componentName: RAM_COMPONENT_NAME, index: number) => {
        switch (componentName) {
            case "RhapsodyDashboard":
            case "QVeraDashboard":
            case "IntersystemsDashboard":

                const dashboardLabelMap: Record<string, string> = {
                    RhapsodyDashboard: "Rhapsody Dashboard",
                    QVeraDashboard: "QVera Dashboard",
                    IntersystemsDashboard: "Intersystems Dashboard",
                };

                const dashboardType = dashboardLabelMap[componentName] ?? "Unknown Dashboard";                
                return <MenuLink 
                    key={`${componentName.toLowerCase()}-link`}
                    onClick={onLinkClick(dashboardType)}
                    className={pageName === dashboardType ? "selected menu-link" : "menu-link"}
                >
                    Dashboard
                </MenuLink>

            case "MonitorServers":
                return <MenuLink key="rhap-servers-link" onClick={onLinkClick("Monitor Servers")} className={pageName === "Monitor Servers" ? "selected menu-link" : "menu-link"}>Servers</MenuLink>
            case "RhapsodyComponents":
                return <MenuLink key="rhap-components-link" onClick={onLinkClick("Rhapsody Components")} className={pageName === "Rhapsody Components" ? "selected menu-link" : "menu-link"}>Components</MenuLink>
            case "IntersystemsComponents":
                return <MenuLink key="int-components-link" onClick={onLinkClick("Intersystems Components")} className={pageName === "Intersystems Components" ? "selected menu-link" : "menu-link"}>Components</MenuLink>
            case "QVeraComponents":
                return <MenuLink key="qve-components-link" onClick={onLinkClick("QVera Components")} className={pageName === "QVera Components" ? "selected menu-link" : "menu-link"}>Components</MenuLink>
            case "QVeraAlerts":
                return <MenuLink key="qve-alerts-link" onClick={onLinkClick("QVera Alerts")} className={pageName === "QVera Alerts" ? "selected menu-link" : "menu-link"}>Alerts</MenuLink>
            case "IntersystemsDiskUsage":
                return <MenuLink key="rhap-disk-usage-link" onClick={onLinkClick("Intersystems Disk Usage")} className={pageName === "Intersystems Disk Usage" ? "selected menu-link" : "header-link"}>Disk Usage</MenuLink>        
            case "MonitorAlerts":
                return <MenuLink key="rhap-alerts-link" onClick={onLinkClick("Monitor Alerts")} className={pageName === "Monitor Alerts" ? "selected menu-link" : "menu-link"}>Alerts</MenuLink>
            case "MonitorQueues":
                return <MenuLink key="rhap-queues-link" onClick={onLinkClick("Monitor Queues")} className={pageName === "Monitor Queues" ? "selected menu-link" : "menu-link"}>Queues</MenuLink>
            case "MonitorCertificates":
                return <MenuLink key="rhap-certificates-link" onClick={onLinkClick("Monitor Certificates")} className={pageName === "Monitor Certificates" ? "selected menu-link" : "header-link"}>Certificates</MenuLink>
            case "MonitorUsers":
                return <MenuLink key="rhap-users-link" onClick={onLinkClick("Monitor Users")} className={pageName === "Monitor Users" ? "selected menu-link" : "menu-link"}>Users</MenuLink>
            case "MonitorMessages":
                return <MenuLink key="rhap-messages-link" onClick={onLinkClick("Monitor Messages")} className={pageName === "Monitor Messages" ? "selected menu-link" : "header-link"}>Messages</MenuLink>
            case "IntersystemsNamespaces":
                return <MenuLink key="rhap-namespaces-link" onClick={onLinkClick("Intersystems Namespaces")} className={pageName === "Intersystems Namespaces" ? "selected menu-link" : "header-link"}>Namespaces</MenuLink>
            case "MonitorTasks":
                return <MenuLink key="rhap-tasks-link" onClick={onLinkClick("Monitor Tasks")} className={pageName === "Monitor Tasks" ? "selected menu-link" : "header-link"}>Tasks</MenuLink>
            case "MonitorEndpoints":
                return <MenuLink key="qver-endpoints-link" onClick={onLinkClick("MonitorEndpoints")} className={pageName === "MonitorEndpoints" ? "selected menu-link" : "header-link"}>Endpoints</MenuLink>
            case "MonitorZones":
                return <MenuLink key="qvera-zones-link" onClick={onLinkClick("MonitorZones")} className={pageName === "MonitorZones" ? "selected menu-link" : "header-link"}>Zones</MenuLink>
            default:
                return null;
        }
    };

    return <TopBar>
        {displayMobileNav && <MobileNavContainer>
            {components.map(c=>c.name).map(getComponentForMobile)}
        </MobileNavContainer>}
        <ActionContainer>
            <ButtonContainer>                
                {components.map(c=>c.name).map(getComponent)}
            </ButtonContainer>
        </ActionContainer>
    </TopBar >;
};

export default RhapsodyMenus;
